import { ITabData } from "@components/tabs";
import { fetchData } from "@odata/OData.utils";
import { logger } from "@utils/log";

import { NEW_ITEM_DETAIL } from "../constants";
import { IAppContext } from "../contexts/appContext/AppContext.types";
import BindingContext, { IEntity, TEntityKey } from "../odata/BindingContext";
import { getCorrectPath, ROUTE_NOT_FOUND } from "../routes";
import { ISplitPageTableDef } from "../views/table/TableView.utils";
import Page, { IPageState, IProps } from "./Page";

export interface IParentPageProps extends IProps {
    isParentEditable: boolean;
    onAfterLoad?: () => void;
}

export default class ParentChildPage<P extends IParentPageProps, S extends IPageState = IPageState> extends Page<P, S> {
    protected parents: IEntity[] = [];

    protected areParentsLoaded = false;
    private areParentsLoading = false;

    constructor(props: P, context: IAppContext) {
        super(props, context);

        this.handleChangeParent = this.handleChangeParent.bind(this);
    }

    async componentDidMount() {
        super.componentDidMount();
        await this.loadParents();
    }

    async componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>) {
        super.componentDidUpdate(prevProps, prevState);
        await this.loadParents();
        this.redirectToFirstParent();
    }

    companyChange(id: number): void {
        this.areParentsLoaded = false;
        this.parents = [];
        super.companyChange(id);
    }


    loadParents = async (): Promise<void> => {
        if (this.definition?.table?.parentDefinition && !this.areParentsLoaded && !this.areParentsLoading) {
            // prevent multiple load in case too many updates are fired
            this.areParentsLoading = true;
            await this.loadParentEntities();
            this.areParentsLoaded = true;
            this.areParentsLoading = false;
            this.redirectToFirstParent();
            this.forceUpdate();
            this.props.onAfterLoad?.();
        }
    };

    loadParentEntities = async (): Promise<void> => {
        const parentDef = (this.masterData.def as ISplitPageTableDef).parentDefinition;
        const bc = this.getTableViewContextMemoized(parentDef.entitySet as string);

        let result: ITabData[];
        try {
            result = await fetchData(bc, this.props.oData, parentDef, this.context);
        } catch (error) {
            // todo: busy state & global error handling
            logger.error("error in fetchData", error);
            result = [];
        }

        this.parents = result;
    };

    get defaultParentKey(): string {
        return this.parents?.[0]?.Id?.toString() ?? "";
    }

    redirectToFirstParent(): void {
        const parents = this.parents || [],
            activeId = this.getParentKey();

        if (parents.length) {
            if (!activeId) {
                if (this.props.dontUseUrlParams) {
                    this.setState({ parentId: parents[0]?.Id });
                } else {
                    const newUrl = getCorrectPath(this.props.match.path, {
                        ParentId: this.defaultParentKey
                    });
                    if (newUrl !== this.props.match.url) {
                        this.props.history.replace(newUrl);
                    }
                }
            } else if (!parents.find((entity: IEntity) => entity.Id?.toString() === activeId)) {
                this.props.history.replace(ROUTE_NOT_FOUND);
            }
        }
    }

    handleRowSelect(bc: BindingContext): void {
        let parentKey, key;
        if (bc.isRoot() && this.props.childCollection) {
            parentKey = bc.getKey();
        } else {
            parentKey = bc.getParent()?.getKey();
            key = bc.getKey();
        }

        this.setUrl(key as string, parentKey as string);
    }

    handleAddDetail(): void {
        const key = this.props.isParentEditable ? null : NEW_ITEM_DETAIL;
        const parentKey = this.props.isParentEditable ? NEW_ITEM_DETAIL : this.getParentKey();
        this.setUrl(key, parentKey);
    }

    handleCloseDetail(): void {
        this.setUrl(null, this.props.isParentEditable ? null : this.getParentKey());
    }

    getParentEntity = (): IEntity => {
        return this.parents?.find((entity: IEntity) => entity.Id.toString() === this.getParentKey());
    };

    getChildCollectionForEditableParent = (): P["childCollection"] => {
        return this.props.isParentEditable && this.props.childCollection;
    };

    getMandatoryChildParentProps = (): {
        parents: IEntity[];
        parentEntity: IEntity;
        onChangeParent: (key?: TEntityKey) => void;
    } => {
        return {
            parents: this.parents,
            parentEntity: this.getParentEntity(),
            onChangeParent: this.handleChangeParent
        };
    };

    handleChangeParent(key?: TEntityKey): void {
        this.setUrl(null, key?.toString(), {}, true);
    }

    handleAfterSave(bc: BindingContext): void {
        const currentKey = bc.getKey() as string;

        // if we have parent-child split page (childCollection prop) we have to differ two cases
        // if parent is not editable we know we are saving child
        // but if it is we have to decide if we are saving child or parent

        const isParentEditing = this.props.isParentEditable && bc.getPath(true) !== this.getChildCollectionForEditableParent();
        const parentKey = isParentEditing ? currentKey : this.getParentKey();
        const key = isParentEditing ? null : currentKey;

        this.setUrl(key, parentKey);
    }

    isReady(): boolean {
        const hasParentDefinition = this.definition?.table?.parentDefinition;
        return super.isReady() && (!hasParentDefinition || this.areParentsLoaded);
    }
}
